/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { COMPARER_START_ID, scrollToAnchor } from './pageScroll';

export type CompareLanguage = 'nl' | 'en';
export type CompareAudience = 'Particulier' | 'Zakelijk' | 'Alle';

/**
 * Where every "Vergelijken" / "Naar de vergelijker" call-to-action sends the
 * visitor: the homepage at `#start`, the comparison's start for the screen it
 * opens on (resolved in lib/pageScroll.ts when the page scrolls). From `sm`
 * that is the TOP of the page: Stap 1 (Bereken — audience, duration, volume)
 * lives in the hero, so the funnel starts there instead of dropping people on
 * the Stap 2 results block with default inputs (2026-08-19). Below `sm` it is
 * the results (2026-10-14, Jasper: "on mobile, ensure all CTA's go directly to
 * the comparison section with the results"): on a phone Stap 1 starts under the
 * whole hero, and the results bar's filter sheet holds the same three inputs.
 * The router's scroll manager (components/ScrollToTop.tsx) does the moving for
 * every hash navigation, the current page included, so a CTA needs no scroll
 * call of its own. `#vergelijken` / `#compare` below stay the results on every
 * screen, for the Stap 1 → Stap 2 hand-off inside the hero and for
 * parametrised deep links (`?audience=…&volume=…#vergelijken`, see
 * public/llms.txt) that already carry the inputs.
 */
export const getComparerHref = (language: CompareLanguage, audience?: CompareAudience): string =>
  `/${language}${audience ? `?audience=${audience}` : ''}#${COMPARER_START_ID}`;

/** A bare comparer address: the homepage of a language, a query at most, no hash. */
const BARE_COMPARER_URL = /^\/(nl|en)\/?(\?[^#]*)?$/;

/**
 * The same start for a call-to-action whose address is CMS data: an article's
 * CTA button holds a bare `/nl` or `/en` on every environment (rewritten so on
 * 2026-08-19 by scripts/rewrite-article-compare-links.mjs, a one-off deleted
 * in 4c35641), and gets `#start`
 * at render — in the bake and the browser alike. Any other address, one with a
 * hash of its own included, is returned as it is.
 */
export const withComparerStart = (href: string): string =>
  BARE_COMPARER_URL.test(href) ? `${href}#${COMPARER_START_ID}` : href;

export const getCompareAnchor = (language: CompareLanguage): string =>
  language === 'nl' ? 'vergelijken' : 'compare';

/**
 * Bring the comparer (Stap 2) into view from a control that does NOT navigate
 * — the filter card's "Toon resultaten" button, the hero's scroll indicator.
 * Links that carry `#vergelijken` / `#compare` need no call: the router's
 * scroll manager (components/ScrollToTop.tsx) handles every hash navigation,
 * and a second scroll from the click handler used to race it with a different
 * offset. One implementation (lib/pageScroll.ts) for both paths, so the
 * comparer always lands at the same spot: just under the header.
 */
export const scrollToCompare = (options?: { delay?: number }): void => {
  const run = () => {
    scrollToAnchor('compare', { waitMs: 0 });
  };
  const delay = options?.delay ?? 0;
  if (delay > 0) setTimeout(run, delay);
  else run();
};
